import React, {
  useContext, useEffect, useState, useRef, Suspense
} from 'react';
import { bool, string, oneOf } from 'prop-types';
import classNames from 'classnames';
import { Image, Carousel, Button } from '@thd-olt-component-react/core-ui';
import { Drawer, DrawerHeader, DrawerItem } from '@thd-olt-component-react/drawer';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useLazyDataModel, useDataModel } from '@thd-nucleus/data-sources';
import { componentReady, thumbnailClick } from '../../../publisher';
import Helper from '../../Helper';
import './media-gallery-carousel.style.scss';
import '../../media-gallery.style.scss';
import { dataModel } from '../../dataModel';
import Threesixty from '../Threesixty/Threesixty';
import {
  VideoPlayerProvider,
  VideoPlayer,
  pauseAllVideos
} from '~/@thd-olt-component-react/video-player/dist/video-player';

const MediaGalleryOverlay = React.lazy(() => import(
  /* webpackChunkName: "media-gallery-overlay" */'../MediaGalleryOverlay'
));
const MediaGalleryOverlayMobile = React.lazy(() => import(
  /* webpackChunkName: "media-gallery-overlay-mobile" */'../MediaGalleryOverlayMobile'
));
const FilmstripMobile = React.lazy(() => import(
  /* webpackChunkName: "media-gallery-filmstrip-mobile" */'../FilmstripMobile'
));

export const MediaGalleryCarousel = ({ itemId, hover, overlayType }) => {
  const experienceContext = useContext(ExperienceContext);
  const carouselWrapperRef = useRef();
  const { channel } = experienceContext;
  const isMobile = channel === 'mobile';

  const [mainAsset, setMainAsset] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [filmstripIsOpen, setFilmstripIsOpen] = useState(false);
  const [threeSixtyOverlayIsOpen, setThreeSixtyOverlayIsOpen] = useState(false);
  const [superSkuHoverId, setSuperSkuHoverId] = useState('');

  const threeSixtyRef = useRef();

  let imgRef = useRef();

  useEffect(() => {
    if (hover) {
      window.LIFE_CYCLE_EVENT_BUS.on('supersku.hover', (hoverItemId) => {
        setSuperSkuHoverId(hoverItemId?.output);
      });
    }
  }, [hover]);

  const [superSkuHoveredProductRequest, superSkuHoveredProductResponse] = useLazyDataModel('product', {
    variables: { itemId: superSkuHoverId }
  });

  useEffect(() => {
    if (superSkuHoverId) {
      superSkuHoveredProductRequest({
        variables: {
          superSkuHoverId
        }
      });
    }
  }, [superSkuHoverId, superSkuHoveredProductRequest]);

  useEffect(() => {
    componentReady({ itemId });
  }, [itemId]);

  // for swatch Hover
  const mainSuperSkuHover = Helper.getMainSuperSkuHover({
    superSkuHoveredProductResponse
  });
  const hasItemId = superSkuHoverId || itemId;
  const productData = useDataModel('product', {
    variables: {
      itemId,
    },
    skip: !hasItemId
  });

  const { product = {} } = productData.data || {};
  const { brandName, productLabel } = product.identifiers || {};
  const { displayedMedia, ...apiResults } = Helper.getDisplayedMedia({
    isMobile, itemId, productData
  });
  if (apiResults.apiState) {
    return apiResults.returnStatement;
  }
  const { thumbnails, main, zoom } = displayedMedia;

  const useFilmstripMobile = overlayType === 'filmstrip' && isMobile;
  const carouselItemClassName = classNames(
    'media-gallery-carousel', {
      'media-gallery-carousel--border': !isMobile,
      'filmstrip-mobile-carousel': useFilmstripMobile
    }
  );

  const handleImageClick = (media) => {
    if (useFilmstripMobile) {
      if (Helper.isImage360({ media })) {
        imgRef.current = media;
        setThreeSixtyOverlayIsOpen(true);
      } else {
        setFilmstripIsOpen(true);
      }
    } else {
      thumbnailClick({ primaryCategory: 'overlay', eventName: 'expanded view' });
      if (typeof window !== 'undefined') {
        window.history.pushState({ page: 'MEDIAOVERLAY' }, null, '');
      }
    }
    setMainAsset(media);
  };

  const onChangeImage = () => {
    setTimeout(() => {
      const slides = carouselWrapperRef.current.querySelectorAll('.swiper-slide');
      const activeSlide = carouselWrapperRef.current.querySelector('.swiper-slide-active');
      const activeIndex = Array(...slides).indexOf(activeSlide);
      setSelectedImageIndex(activeIndex);
    }, 1);
  };

  return (
    <div className={carouselItemClassName} ref={carouselWrapperRef}>
      <VideoPlayerProvider autoPlayInViewport={(overlayType === 'filmstrip')}>
        <Carousel
          pagination
          dynamicPagination={useFilmstripMobile}
          peek={false}
          breakpoints={{
            sm: {
              slidesPerView: 1,
              slidesPerGroup: 1
            },
            md: {
              slidesPerView: 1,
              slidesPerGroup: 1
            },
            lg: {
              slidesPerView: 1,
              slidesPerGroup: 1
            },
            xl: {
              slidesPerView: 1,
              slidesPerGroup: 1
            }
          }}
          slidesPerView={1}
          onSlideChange={() => onChangeImage()}
        >
          {
            displayedMedia.main
              .filter((media) => (useFilmstripMobile
                ? !Helper.isAR({ media })
                : true))
              .map((media, mediaIndex) => {
                const {
                  shortDescription, url, mediaStill, videoStill
                } = media;
                const isVideo = Helper.isVideo({ media });
                const isThreeSixty = Helper.isImage360({ media });
                const isImage = Helper.isImage({ media });
                const isAR = Helper.isAR({ media });
                const isHotspot = Helper.isHotspot({ media });
                let imageUrl = url;
                let index = 0;

                const { source, videoSource, videoId } = Helper.formatVideoProps(media);

                if (mediaIndex === selectedImageIndex) {
                  if (isImage) {
                    index = mainSuperSkuHover.findIndex((superSkuHoverMedia) => {
                      return superSkuHoverMedia.type === main[selectedImageIndex].type;
                    });
                    if (index > 0 && mainSuperSkuHover[index]) {
                      imageUrl = mainSuperSkuHover[index].url;
                    }
                    if (index <= 0 && mainSuperSkuHover[0]) {
                      imageUrl = mainSuperSkuHover[0].url;
                    }
                  } else if (isVideo) {
                    imageUrl = mainSuperSkuHover[0] ? mainSuperSkuHover[0].url : videoStill;
                  } else if (isThreeSixty || isAR) {
                    imageUrl = mainSuperSkuHover[0] ? mainSuperSkuHover[0].url : mediaStill;
                  }
                } else if (isVideo) {
                  imageUrl = videoStill;
                } else if (isThreeSixty || isAR) {
                  imageUrl = mediaStill;
                }
                const videoScriptContent = JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'VideoObject',
                  name: productLabel,
                  description: shortDescription,
                  thumbnailUrl: [videoStill],
                  contentUrl: url
                });
                const videoPlayerClassNames = classNames(
                  'static-position', {
                    'media-gallery-carousel__video-slide--active': mediaIndex === selectedImageIndex
                  }
                );

                return (
                  <div className="media-gallery-carousel__slide" key={mediaIndex}>
                    { (useFilmstripMobile && isVideo)
                      ? (
                        <VideoPlayer
                          className={videoPlayerClassNames}
                          key={media.id}
                          videoId={videoId}
                          source={source}
                          videoSrc={videoSource}
                          aspectRatio={null}
                        />
                      )
                      : (
                        <>
                          <Image
                            onClick={() => handleImageClick(media, mediaIndex)}
                            className="media-gallery-carousel__slide--active"
                            alt={`${brandName} - ${productLabel}`}
                            src={imageUrl}
                            lazy
                          />
                          {!isImage
                          && (!mainSuperSkuHover[0] || mediaIndex !== selectedImageIndex)
                          && (
                            <button type="button" onClick={() => handleImageClick(media, mediaIndex)}>
                              <span className="background-circle background-circle--main" />
                              {isThreeSixty && (<span className="threesixty-icon threesixty-icon--main" />)}
                              {isAR && (<span className="ar-icon ar-icon--main" />)}
                              {isHotspot && (<span className="hotspot-tag-icon hotspot-tag-icon__main" />)}
                              { !useFilmstripMobile && isVideo
                                && (
                                  <>
                                    <span className="play-icon play-icon--main" />
                                    <script
                                      type="application/ld+json"
                                      /* eslint-disable-next-line react/no-danger */
                                      dangerouslySetInnerHTML={{ __html: videoScriptContent }}
                                    />
                                  </>
                                )}
                            </button>
                          )}

                        </>
                      )}
                  </div>
                );
              })
          }
        </Carousel>
      </VideoPlayerProvider>
      {!!mainAsset
      && (
        <Suspense fallback={(<div />)}>
          { !useFilmstripMobile
            && ((!isMobile
                && (
                  <MediaGalleryOverlay
                    handleClose={() => setMainAsset(null)}
                    overlayOpened={!!mainAsset}
                    media={displayedMedia}
                    product={product}
                    mainAsset={mainAsset}
                    thumbnails={thumbnails}
                  />
                ))
            || (isMobile
                && (
                  <MediaGalleryOverlayMobile
                    handleClose={() => setMainAsset(null)}
                    overlayOpened={!!mainAsset}
                    product={product}
                    mainImage={mainAsset}
                    mainImages={main}
                    onMediaClick={handleImageClick}
                    zoomedImages={zoom}
                    thumbnails={thumbnails}
                    selectedImageIndex={selectedImageIndex}
                  />
                )
            ))}
          { useFilmstripMobile
            && (
              <>
                <FilmstripMobile
                  overrideData={{ media: product.media, brandName, galleryLabel: productLabel }}
                  isMobile
                  isOpen={filmstripIsOpen}
                  setIsOpen={setFilmstripIsOpen}
                  showLoader
                />
                <Drawer
                  initialItem="threeSixty"
                  onClose={() => {
                    setThreeSixtyOverlayIsOpen(false);
                    threeSixtyRef.current.resetImage();
                  }}
                  open={threeSixtyOverlayIsOpen}
                  maxSize={100}
                  isMobile
                  isFlex
                  persistOnOrientationChange
                >
                  <DrawerItem name="threeSixty">
                    <DrawerHeader />
                    <div className="drawer-media">
                      <div className="three-sixty__wrapper">
                        <Threesixty media={imgRef.current || {}} fwdImageRef={imgRef} ref={threeSixtyRef} />
                      </div>
                      <div className="reset-bttn__wrapper">
                        <Button
                          className="reset-bttn"
                          outline
                          onClick={() => { threeSixtyRef.current.resetImage(); }}
                        >Reset
                        </Button>
                      </div>
                    </div>
                  </DrawerItem>
                </Drawer>
              </>
            )}
        </Suspense>
      )}
    </div>
  );
};

MediaGalleryCarousel.displayName = 'MediaGalleryCarousel';

MediaGalleryCarousel.propTypes = {
  hover: bool,
  itemId: string.isRequired,
  overlayType: oneOf([
    'default',
    'filmstrip' // filmstrip is currently only UX-approved for mobile
  ])
};

MediaGalleryCarousel.defaultProps = {
  hover: false,
  overlayType: 'default'
};

MediaGalleryCarousel.dataModel = dataModel;
